import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { AuthService } from '../services/auth.service';

/**
 * Plan Guard Factory
 * Restricts routes to specific subscription plans.
 * If the business's planId is not in the allowed list, redirects to /dashboard.
 *
 * Taxonomy (AC-3 / HU refactor-de-planes-paycaja):
 *   New plan ids: 'solo-fact' | 'pay-caja' | 'todo-incluido' | 'custom'.
 *   Businesses with a missing/undefined planId fall back to 'pay-caja'
 *   (replaces the legacy 'full' fallback).
 *   Any planId outside the new taxonomy (legacy 'dian'/'full'/'premium' or
 *   otherwise unknown) is left untouched but logged via console.warn so it can
 *   be flagged until the migration (migratePlansTaxonomy) runs — Q3 decision:
 *   no UX change, the silent-redirect behavior is preserved.
 *
 * Usage:
 * canActivate: [planGuard(['pay-caja', 'todo-incluido'])]
 */
const KNOWN_PLAN_IDS = ['solo-fact', 'pay-caja', 'todo-incluido', 'custom'];

export function planGuard(allowedPlans: string[]): CanActivateFn {
  return async (route, state) => {
    const authService = inject(AuthService);
    const router = inject(Router);

    await authService.waitForAuth();

    const context = authService.currentBusinessContext();
    const rawPlanId = context?.business?.subscription?.planId;
    // Missing planId → treat as 'pay-caja' for backward compat (replaces legacy 'full').
    const planId = rawPlanId || 'pay-caja';

    // Observability (Q3): flag legacy/unknown ids until the migration runs.
    // No UX change — this only logs; the redirect logic below is unchanged.
    if (rawPlanId && !KNOWN_PLAN_IDS.includes(rawPlanId)) {
      console.warn(
        `Plan guard: legacy/unknown planId '${rawPlanId}' encountered ` +
          `(not in new taxonomy [${KNOWN_PLAN_IDS.join(', ')}]). ` +
          `Run migratePlansTaxonomy to migrate this business.`
      );
    }

    if (allowedPlans.includes(planId)) {
      return true;
    }

    console.warn(
      `Plan guard: planId '${planId}' not in allowed plans [${allowedPlans.join(', ')}]`
    );
    router.navigate(['/dashboard']);
    return false;
  };
}
